import {
  BiodirectNoDataIcon,
  closeIconV2,
  ExternalLinkIcon,
  inforIcon,
  logoIcon,
} from '@/shared/ui/assets';
import { NoDataComponent, CustomButton, LoadingDrawer } from '@/shared/ui/components';
import { zeroPad, gettextStudyStatus } from '@/shared/lib';
import { Drawer } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import StudyTypeFormatter from '../studyTypeFormatter';
import { NOT_ASSIGNED_MESSAGE } from '../../lib/notAssignedMessage';
import { useStudyInfoDrawer } from './useStudyInfoDrawer';
import type { StudyInfoDrawerProps } from './studyInfoDrawer.types';
import './studyInfoDrawer.scss';

const StudyInfoDrawer = ({
  visible,
  handleClickCloseButton,
  fetchStudyId,
  isOngoing = false,
  isFlaggedStudiesTab = false,
}: StudyInfoDrawerProps) => {
  const { state, blockInterprettingPhysicianViewStudy, handleScroll } = useStudyInfoDrawer({
    visible,
    handleClickCloseButton,
    fetchStudyId,
    isOngoing,
    isFlaggedStudiesTab,
  });

  const closeButton = (
    <CustomButton
      id="close-btn"
      style="neutral"
      type="tertiary"
      size="small"
      icon={<ReactSVG src={closeIconV2} />}
      onClick={handleClickCloseButton}
      ariaLabel={t`Close`}
    />
  );

  return (
    <Drawer
      id="study-info-drawer-id"
      aria-label={`${t`Study`} ${zeroPad(state.study?.friendlyId)}`}
      rootClassName="custom-drawer study-info-drawer"
      width={500}
      closable={false}
      onClose={handleClickCloseButton}
      open={visible}
    >
      <LoadingDrawer isLoading={state.isLoading} rightIcon={closeButton}>
        <div
          className={classNames('drawer-title-container', state.isScroll ? '--has-box-shadow' : '')}
        >
          <div className="container-top d-flex">
            <div className="drawer-title">
              <span className="title">{`${t`Study`} ${zeroPad(state.study?.friendlyId)}`}</span>
            </div>
            {closeButton}
          </div>
        </div>
        {state.isNotAssigned ? (
          <div id="drawer-body-container" className="drawer-body-container">
            <div className="not-assigned-container">
              <ReactSVG src={BiodirectNoDataIcon} />
              <span className="message">{NOT_ASSIGNED_MESSAGE}</span>
            </div>
          </div>
        ) : blockInterprettingPhysicianViewStudy ? (
          <div id="drawer-body-container" className="drawer-body-container" onScroll={handleScroll}>
            <NoDataComponent
              className="no-data-study-drawer"
              message={t`You are not the interpreting physician of this study!`}
            />
          </div>
        ) : (
          <div id="drawer-body-container" className="drawer-body-container" onScroll={handleScroll}>
            <div className="button-container">
              <CustomButton
                id="external-link-btn"
                className="open-study-management-button"
                style="default"
                type="secondary"
                onClick={() => {
                  window.open(`/studies/${state.study?.id}`);
                }}
              >
                <div className="button-content-container">
                  <span>{t`Open study management`}</span>
                  <span aria-hidden="true">
                    <ReactSVG src={ExternalLinkIcon} />
                  </span>
                </div>
              </CustomButton>
            </div>
            {state.isAbortedStudy && (
              <div className="aborted-study-message-container">
                <div className="aborted-study-message">
                  <ReactSVG src={inforIcon} />
                  <span>
                    {t`This study has been aborted and will not be counted toward your billing.`}
                  </span>
                </div>
              </div>
            )}
            {!_.isEmpty(state.study?.biofluxDirect) && (
              <div className="biodirect-label-container">
                <div className="biodirect-label">
                  <ReactSVG src={logoIcon} />
                  <span>{t`Biodirect service has been applied to this study`}</span>
                </div>
              </div>
            )}
            <div>
              {state.data
                .filter((x) => !!x)
                .map((element) => {
                  if (element.header === t`Physician amendment notes`) {
                    return (
                      <div key={element.header} className="drawer-body">
                        {!!element.header && (
                          <div className="drawer-header">
                            <span>{element.header}</span>
                          </div>
                        )}
                        <div className="drawer-content">
                          <div className="notes">{element.content}</div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={element.header} className="drawer-body">
                      {!!element.header && (
                        <div className="drawer-header">
                          <span>{element.header}</span>
                        </div>
                      )}
                      <div
                        className={classNames('drawer-content', {
                          'fo-study': element.wrapperClassName,
                        })}
                      >
                        {_.map(
                          _.filter(element.content, (x) => !_.isEmpty(x)),
                          (item) => {
                            if (element.wrapperClassName) {
                              return item;
                            }

                            return (
                              <div
                                key={item.label}
                                className={classNames('drawer-item', element.className || '')}
                              >
                                <span>{`${item.label}:`}</span>
                                {item.label === 'Status' ? (
                                  <div className="study-status-container">
                                    <span>{gettextStudyStatus(item.value)}</span>
                                  </div>
                                ) : item.label === 'Study type' ? (
                                  <StudyTypeFormatter
                                    studyType={item.value}
                                    isFollowOnStudy={
                                      (item.linkedStudies && item.linkedStudies.length) ||
                                      item?.followOnStudy
                                    }
                                    isTextButton={item.isTextButton}
                                    handleClick={item.handleClick}
                                    isDisabled={item.isDisabled}
                                  />
                                ) : (
                                  <span>{item.value}</span>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="additional-div" />
          </div>
        )}
      </LoadingDrawer>
    </Drawer>
  );
};

export default StudyInfoDrawer;
